/**
 * TaskModalSidebar - Sidebar contextual do modal de tarefa
 * 
 * Seções: Informações Básicas, Fases e Status, Relacionamentos, Cronograma, Pessoas.
 * 
 * @version 5.3.0
 * RELEASE: 2026-08-22 v5.3.0 - Cronograma do Jurídico exibe "Data de Entrada" e novos cards
 *          "Dados de Jurídico" / "Dados de Retenção" com os campos do módulo
 * RELEASE: 2026-08-21 v5.2.0 - Cronograma exibe "Data Solicitação" (Retenção) e "Prazo Processual" (Jurídico)
 * RELEASE: 2026-04-07 v5.0.0 - Trocar ícone Pencil→ExternalLink, modal de alteração de responsável
 * RELEASE: 2026-04-07 v4.0.0 - Reestruturar seções INFORMAÇÕES BÁSICAS e FASES E STATUS
 * RELEASE: 2026-04-07 v3.0.0 - Adicionar seção RELACIONAMENTOS
 * RELEASE: 2026-04-07 v2.0.0 - Redesign com seções Cronograma e Pessoas
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FolderTree,
  Target,
  Tags,
  Calendar,
  CalendarClock,
  Users,
  UserCircle,
  Briefcase,
  Clock,
  GitBranch,
  User,
  ExternalLink,
  Link2,
  Activity,
  AlertTriangle,
  Layers,
  Flag,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  XCircle,
  Timer,
  ClipboardList,
} from 'lucide-react';
import { formatDateBR } from '@/lib/format-date';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TaskRelationshipsModal } from '@/components/tasks/modal/TaskRelationshipsModal';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { TaskDurationInfo } from '@/components/tasks/modal/components/TaskDurationInfo';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import type { TaskDetail } from '@/hooks/useTaskDetails';
import { TASK_MODULES, type TaskModuleType } from '@/hooks/useTaskModuleType';

/**
 * Mapa de estilos de status (reutilizado do TaskModalHeader)
 */
const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pendente', className: 'inline-flex items-center rounded-full font-medium bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-slate-500 before:mr-1.5' },
  in_progress: { label: 'Andamento', className: 'inline-flex items-center rounded-full font-medium bg-blue-50 text-blue-700 hover:bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-blue-500 before:mr-1.5' },
  paused: { label: 'Pausada', className: 'inline-flex items-center rounded-full font-medium bg-amber-50 text-amber-800 hover:bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-amber-500 before:mr-1.5' },
  completed: { label: 'Concluída', className: 'inline-flex items-center rounded-full font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-emerald-500 before:mr-1.5' },
  cancelled: { label: 'Cancelada', className: 'inline-flex items-center rounded-full font-medium bg-rose-50 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-rose-500 before:mr-1.5' },
};

/**
 * Componente de info rápida (ícone + label + valor)
 */
function QuickInfo({ icon: Icon, label, value, color }: {
  icon: typeof FolderTree;
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="flex items-center gap-2 py-1.5 w-full">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 ml-auto">
        {color && (
          <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
        )}
        <span className="text-xs font-medium truncate max-w-[200px]">{value}</span>
      </div>
    </div>
  );
}

/**
 * Linha de cronograma (ícone + label + data/hora à direita)
 * @param colorClass - Classe CSS opcional para cor do ícone e label (ex: 'text-green-600')
 * @param dateOnly - v5.2.0: quando o valor é um DATE puro (yyyy-MM-dd), evita
 *                   deslocamento de fuso e oculta o horário
 */
function ScheduleRow({ icon: Icon, label, dateStr, colorClass, dateOnly }: {
  icon: typeof Calendar;
  label: string;
  dateStr: string | null;
  colorClass?: string;
  dateOnly?: boolean;
}) {
  if (!dateStr) {
    return (
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-2">
          <Icon className={`h-3.5 w-3.5 ${colorClass || 'text-muted-foreground'}`} />
          <span className={`text-xs ${colorClass || 'text-muted-foreground'}`}>{label}</span>
        </div>
        <span className="text-xs text-muted-foreground">—</span>
      </div>
    );
  }

  // Datas puras (yyyy-MM-dd) são interpretadas no horário local ao meio-dia
  const date = dateOnly ? new Date(`${dateStr.slice(0, 10)}T12:00:00`) : new Date(dateStr);
  const formattedDate = format(date, 'dd/MM/yyyy', { locale: ptBR });
  const formattedTime = format(date, 'HH:mm', { locale: ptBR });

  return (
    <div className="flex items-center justify-between py-0.5">
      <div className="flex items-center gap-2">
        <Icon className={`h-3.5 w-3.5 ${colorClass || 'text-muted-foreground'}`} />
        <span className={`text-xs ${colorClass || 'text-muted-foreground'}`}>{label}</span>
      </div>
      <div className="text-right">
        <span className={`text-xs font-medium ${colorClass || ''}`}>{formattedDate}</span>
        {!dateOnly && (
          <span className="text-[10px] text-muted-foreground ml-1">{formattedTime}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Formata valor monetário em BRL. Retorna null quando vazio/zero-nulo.
 * v5.3.0
 */
function formatBRL(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Linha simples de dados do módulo (label à esquerda, valor à direita)
 * Usada nos cards "Dados de Jurídico" e "Dados de Retenção" (v5.3.0)
 */
function ModuleDataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 py-1 w-full">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="ml-auto text-xs font-medium text-right truncate max-w-[190px]">
              {value}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p className="text-xs max-w-[240px] break-words">{value}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}




/**
 * Calcula duração legível entre duas datas
 * Retorna string como "3 dias", "5 horas", "45 min"
 */
function calcDurationLabel(startStr: string, endStr: string | null): string | null {
  if (!startStr) return null;
  const start = new Date(startStr).getTime();
  const end = endStr ? new Date(endStr).getTime() : Date.now();
  const diffMs = end - start;
  if (diffMs <= 0) return null;

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days} dia${days > 1 ? 's' : ''}`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hora${hours > 1 ? 's' : ''}`;
  return `${Math.max(1, minutes)} min`;
}

/**
 * Linha de relacionamento (ícone + label + valor ou "Não vinculado" + olho para visualizar)
 */
/**
 * Linha de relacionamento (ícone + label + valor ou "Não vinculado" + olho para visualizar)
 * v5.1.0: Suporte a disabled com tooltip para permissão negada
 */
function RelationshipRow({ icon: Icon, label, value, onView, disabled, disabledTooltip }: {
  icon: typeof User;
  label: string;
  value: string | null;
  /** Callback para abrir modal de detalhes do registro vinculado */
  onView?: () => void;
  /** Se true, exibe ícone desabilitado ao invés do olho clicável */
  disabled?: boolean;
  /** Tooltip exibido quando disabled */
  disabledTooltip?: string;
}) {
  return (
    <div className="flex items-center gap-2 py-1.5 w-full">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 ml-auto min-w-0">
        <span className={`text-xs truncate max-w-[180px] ${value ? 'font-medium' : 'text-muted-foreground italic'}`}>
          {value || 'Não vinculado'}
        </span>
        {value && disabled && disabledTooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 p-0.5">
                  <EyeOff className="h-3.5 w-3.5 text-muted-foreground/40" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="text-xs">{disabledTooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {value && !disabled && onView && (
          <button
            type="button"
            onClick={onView}
            className="shrink-0 p-0.5 rounded hover:bg-muted transition-colors"
            title={`Visualizar ${label}`}
          >
            <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}

interface TaskModalSidebarProps {
  task: TaskDetail;
  /** Função para atualizar tarefa (usada pelo modal de relacionamentos) */
  onUpdateTask?: (params: Record<string, any>) => Promise<void>;
  isUpdating?: boolean;
  /** Classes CSS adicionais (permite override de largura no mobile) */
  className?: string;
}

/**
 * Sidebar contextual (360px fixa)
 */
export function TaskModalSidebar({ task, onUpdateTask, isUpdating, className }: TaskModalSidebarProps) {
  const [showRelModal, setShowRelModal] = useState(false);
  /** Estado do modal de alteração de responsável */
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [newAssigneeId, setNewAssigneeId] = useState<string | null>(null);
  const [isSavingAssignee, setIsSavingAssignee] = useState(false);
  /** Estados para modais de visualização de registros vinculados */
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showParentTaskModal, setShowParentTaskModal] = useState(false);
  // v5.1.0: Layout responsivo — remove fundo/borda/padding extra no mobile para ganhar largura
  const isMobile = useIsMobile();

  // Formata data de criação para exibir na seção Pessoas
  const createdAtFormatted = task.created_at
    ? format(new Date(task.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : '-';

  /**
   * Calcula texto e estilo do badge de prazo
   * Retorna null se tarefa concluída/cancelada
   */
  const getDeadlineBadge = () => {
    // Tarefa concluída ou cancelada não exibe badge
    if (task.status === 'completed' || task.status === 'cancelled') return null;

    // Sem prazo definido
    if (!task.due_date) {
      return {
        text: 'Sem prazo',
        className: 'bg-muted/50 text-muted-foreground',
        icon: Clock,
      };
    }

    const now = new Date();
    const due = new Date(task.due_date);
    const diffMs = due.getTime() - now.getTime();
    const absDiffMs = Math.abs(diffMs);

    // Formata duração em dias/horas/minutos
    const totalMinutes = Math.floor(absDiffMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    let timeText = '';
    if (days > 0) {
      timeText = `${days}d ${hours}h`;
    } else if (hours > 0) {
      timeText = `${hours}h ${minutes}m`;
    } else {
      timeText = `${Math.max(1, minutes)}m`;
    }

    if (diffMs < 0) {
      // Vencido
      return {
        text: `Vencido: ${timeText}`,
        className: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
        icon: AlertTriangle,
      };
    }

    // Restam
    return {
      text: `Restam: ${timeText}`,
      className: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
      icon: Clock,
    };
  };

  const deadlineBadge = getDeadlineBadge();

  // Status config
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.in_progress;

  // Formata tarefa pai para exibição
  const parentTaskDisplay = task.parent_task_id
    ? `#${task.parent_task_number} ${task.parent_task_title}`
    : null;

  return (
    <>
      <ScrollArea className={cn(
        "h-full shrink-0",
        isMobile ? "w-full" : "w-[360px] bg-muted/30 border-l border-border/40",
        className
      )}>
        <div className={cn("space-y-3", isMobile ? "p-0" : "p-3")}>
          {/* ═══════════════════════════════════════════ */}
          {/* INFORMAÇÕES BÁSICAS — card flutuante       */}
          {/* ═══════════════════════════════════════════ */}
          <div className="rounded-xl border border-border bg-card shadow-sm p-3">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Informações Básicas
            </h4>

            {/* Badge de prazo dinâmico */}
            {deadlineBadge && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-md mb-3 ${deadlineBadge.className}`}>
                <deadlineBadge.icon className="h-3.5 w-3.5 shrink-0" />
                <span className="text-xs font-medium">{deadlineBadge.text}</span>
              </div>
            )}

            {/* Duração efetiva da tarefa */}
            <TaskDurationInfo task={task} />

            <div className="space-y-1">
              {/* Tipo da tarefa — identificação rápida do módulo (normal/retenção/jurídico) */}
              <QuickInfo
                icon={Layers}
                label="Tipo"
                value={TASK_MODULES[(task.task_type || 'normal') as TaskModuleType]?.label || 'Normal'}
              />

              {/* Prazo */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Prazo</span>
                <div className="ml-auto text-right">
                  {task.due_date ? (
                    <>
                      <span className="text-xs font-medium">
                        {format(new Date(task.due_date), 'dd/MM/yyyy', { locale: ptBR })}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-1">
                        {format(new Date(task.due_date), 'HH:mm', { locale: ptBR })}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {/* Classificação */}
              {task.classification_name && (
                <QuickInfo
                  icon={Layers}
                  label="Classificação"
                  value={task.classification_name}
                  color={task.classification_color || undefined}
                />
              )}

              {/* Prioridade */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Flag className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Prioridade</span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <PriorityFlag priority={task.priority} size={12} />
                  <span className="text-xs font-medium">
                    {PRIORITY_LABELS[task.priority] || 'Normal'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════ */}
          {/* FASES E STATUS — card flutuante            */}
          {/* ═══════════════════════════════════════════ */}
          <div className="rounded-xl border border-border bg-card shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Fases e Status
              </h4>
              <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              {/* Categoria */}
              <QuickInfo
                icon={Tags}
                label="Categoria"
                value={task.category_name}
                color={task.category_color}
              />

              {/* Fase com barra de progresso */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Fase</span>
                <div className="ml-auto">
                  <DealPhaseProgress
                    phaseName={task.phase_name}
                    phaseColor={task.phase_color}
                    phaseSortOrder={task.phase_sort_order || 1}
                    totalPhases={task.total_category_phases || 1}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Activity className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Status</span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <Badge className={`text-[10px] h-5 px-2 ${statusConfig.className}`}>
                    {statusConfig.label}
                  </Badge>
                  {deadlineBadge && deadlineBadge.icon === AlertTriangle && (
                    <Badge variant="destructive" className="text-[9px] px-1.5 py-0 h-5">
                      Vencida
                    </Badge>
                  )}
                </div>
              </div>

              {/* Motivo da conclusão (Retenção/Jurídico) — informado ao concluir */}
              {(task as any).completion_reason_name && (
                <div className="flex items-start gap-2 py-1.5 w-full">
                  <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <span className="text-xs text-muted-foreground shrink-0">Motivo</span>
                  <div className="ml-auto flex flex-col items-end gap-1 min-w-0">
                    <Badge
                      variant="outline"
                      className="text-[10px] h-5 px-2 max-w-[170px]"
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-1 shrink-0"
                        style={{
                          backgroundColor:
                            (task as any).completion_reason_color ||
                            'hsl(var(--muted-foreground))',
                        }}
                      />
                      <span className="truncate">
                        {(task as any).completion_reason_name}
                      </span>
                    </Badge>
                    {(task as any).completion_note && (
                      <p className="text-[11px] text-muted-foreground text-right break-words [overflow-wrap:anywhere] max-w-[190px]">
                        {(task as any).completion_note}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════ */}
          {/* DADOS DO MÓDULO (Jurídico / Retenção)      */}
          {/* v5.3.0 — exibe os campos preenchidos na    */}
          {/* criação da tarefa (tabelas satélites)      */}
          {/* ═══════════════════════════════════════════ */}
          {task.task_type === 'legal' && (() => {
            const ld = (task.legal_details as any) || {};
            const rows: Array<[string, string | null]> = [
              ['Número do processo', ld.process_number || null],
              ['Advogado responsável', ld.lawyer || null],
              ['Tipo de ação', ld.action_type || null],
              ['Tribunal / Vara', ld.court || null],
              ['Valor da causa', formatBRL(ld.case_value)],
              ['Valor original da dívida', formatBRL(ld.original_debt_value)],
            ];
            const visible = rows.filter(([, v]) => !!v);
            if (visible.length === 0) return null;
            return (
              <div className="rounded-xl border border-border bg-card shadow-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Dados de Jurídico
                  </h4>
                  <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  {visible.map(([label, value]) => (
                    <ModuleDataRow key={label} label={label} value={value as string} />
                  ))}
                </div>
              </div>
            );
          })()}

          {task.task_type === 'retention' && (() => {
            const rd = (task.retention_details as any) || {};
            const rows: Array<[string, string | null]> = [
              ['Motivo do cancelamento', rd.cancel_reason || null],
              ['Desfecho', rd.outcome || null],
              ['Valor em risco', formatBRL(rd.value_at_risk)],
              ['Concorrente', rd.competitor || null],
              ['Data efetiva', rd.effective_date ? formatDateBR(rd.effective_date) : null],
            ];
            const visible = rows.filter(([, v]) => !!v);
            if (visible.length === 0) return null;
            return (
              <div className="rounded-xl border border-border bg-card shadow-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Dados de Retenção
                  </h4>
                  <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  {visible.map(([label, value]) => (
                    <ModuleDataRow key={label} label={label} value={value as string} />
                  ))}
                </div>
              </div>
            );
          })()}



          {/* ═══════════════════════════════════════════ */}
          {/* RELACIONAMENTOS — card flutuante           */}
          {/* ═══════════════════════════════════════════ */}
          <div className="rounded-xl border border-border bg-card shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Relacionamentos
              </h4>
              {onUpdateTask && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="h-5 w-5"
                  onClick={() => setShowRelModal(true)}
                >
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                </Button>
              )}
            </div>
            <div className="space-y-1">
              <RelationshipRow
                icon={User}
                label="Cliente"
                value={task.contact_name}
                onView={task.customer_id ? () => setShowCustomerModal(true) : undefined}
              />
              <RelationshipRow
                icon={Briefcase}
                label="Negócio"
                value={task.deal_title}
                onView={task.deal_id ? () => setShowDealModal(true) : undefined}
              />
              <RelationshipRow
                icon={GitBranch}
                label="Tarefa Pai"
                value={parentTaskDisplay}
                onView={task.parent_task_id ? () => setShowParentTaskModal(true) : undefined}
                disabled={task.parent_task_id ? !task.can_view_parent : false}
                disabledTooltip="Sem permissão para visualizar"
              />
            </div>
          </div>

          {/* ═══════════════════════════════════════════ */}
          {/* CRONOGRAMA — card flutuante                */}
          {/* ═══════════════════════════════════════════ */}
          <div className="rounded-xl border border-border bg-card shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Cronograma
              </h4>
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              {/* v5.2.0 — Retenção: data em que o cliente fez a solicitação */}
              {task.task_type === 'retention' && (
                <ScheduleRow
                  icon={CalendarClock}
                  label="Data Solicitação"
                  dateStr={(task.retention_details as any)?.requested_at ?? null}
                  dateOnly
                />
              )}

              {/* v5.3.0 — Jurídico: data de entrada do caso */}
              {task.task_type === 'legal' && (
                <ScheduleRow
                  icon={CalendarClock}
                  label="Data de Entrada"
                  dateStr={(task.legal_details as any)?.entry_date ?? null}
                  dateOnly
                />
              )}

              {/* v5.2.0 — Jurídico: prazo processual */}

              {task.task_type === 'legal' && (
                <ScheduleRow
                  icon={Clock}
                  label="Prazo Processual"
                  dateStr={(task.legal_details as any)?.deadline_date ?? null}
                  dateOnly
                  colorClass="text-amber-600 dark:text-amber-400"
                />
              )}

              {/* Criação */}
              <ScheduleRow
                icon={Calendar}
                label="Criação"
                dateStr={task.created_at}
              />


              {/* Início */}
              <ScheduleRow
                icon={CalendarClock}
                label="Início"
                dateStr={task.started_at}
              />

              {/* Alteração Fase — data em que a tarefa entrou na fase atual */}
              <ScheduleRow
                icon={GitBranch}
                label="Alteração Fase"
                dateStr={task.phase_updated_at}
              />


              {/* Linha condicional por status */}
              {task.status === 'completed' && (
                <ScheduleRow
                  icon={CheckCircle2}
                  label="Conclusão"
                  dateStr={task.completed_at}
                  colorClass="text-green-600 dark:text-green-400"
                />
              )}

              {task.status === 'cancelled' && (
                <ScheduleRow
                  icon={XCircle}
                  label="Cancelamento"
                  dateStr={task.cancelled_at}
                  colorClass="text-red-600 dark:text-red-400"
                />
              )}

              {(task.status === 'pending' || task.status === 'in_progress' || task.status === 'paused') && task.due_date && (
                <ScheduleRow
                  icon={Clock}
                  label="Vencimento"
                  dateStr={task.due_date}
                  colorClass="text-amber-600 dark:text-amber-400"
                />
              )}

              {/* Barra de duração — exibe apenas se tarefa foi iniciada */}
              {task.started_at && task.status === 'completed' && (() => {
                // Duração no cronograma: exibida apenas para tarefas concluídas
                const durationLabel = calcDurationLabel(task.started_at, task.completed_at);
                if (!durationLabel) return null;

                return (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md mt-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <Timer className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
                    <span className="text-xs font-medium text-green-600 dark:text-green-400">
                      Duração: {durationLabel}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ═══════════════════════════════════════════ */}
          {/* PESSOAS — card flutuante                   */}
          {/* ═══════════════════════════════════════════ */}
          <div className="rounded-xl border border-border bg-card shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Pessoas
              </h4>
              <Users className="h-3.5 w-3.5 text-muted-foreground" />
            </div>

            {/* Criado por */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <UserCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Criado por</span>
              </div>
              {task.created_by ? (
                <div className="flex items-center gap-2">
                  <SmartAvatar
                    src={task.created_by_avatar}
                    name={task.created_by_name}
                    size="xs"
                    use3DFallback
                  />
                  <div className="text-right">
                    <p className="text-sm font-medium">{task.created_by_name || 'Sistema'}</p>
                    <p className="text-[10px] text-muted-foreground">{createdAtFormatted}</p>
                  </div>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground italic">-</span>
              )}
            </div>

            {/* Responsável */}
            <div className="ml-2 mt-3 bg-muted/30 rounded-lg p-3">
              <div className="flex items-start gap-3 py-2">
                <div className="mt-0.5">
                  <UserCircle className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Responsável
                    </span>
                    {onUpdateTask && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="h-5 w-5"
                        onClick={() => {
                          setNewAssigneeId(task.assigned_to || null);
                          setShowAssigneeModal(true);
                        }}
                      >
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                  {task.assigned_to ? (
                    <div className="flex items-center gap-2 mt-1">
                      <SmartAvatar
                        src={task.assigned_to_avatar}
                        name={task.assigned_to_name}
                        size="xs"
                        use3DFallback
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {task.assigned_to_name || 'Sem nome'}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          Responsável da tarefa
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-6 w-6 rounded-full bg-muted/50 flex items-center justify-center">
                        <UserCircle className="h-4 w-4 text-muted-foreground/50" />
                      </div>
                      <p className="text-sm text-muted-foreground italic">
                        Não definido
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Modal de edição de relacionamentos */}
      {onUpdateTask && (
        <TaskRelationshipsModal
          open={showRelModal}
          onOpenChange={setShowRelModal}
          task={task}
          onSave={onUpdateTask}
          isSaving={isUpdating}
        />
      )}

      {/* Modal de detalhes do cliente */}
      <CustomerDetailsModalPremium
        customerId={task.customer_id || null}
        open={showCustomerModal}
        onOpenChange={setShowCustomerModal}
      />

      {/* Modal de detalhes do negócio */}
      <DealDetailsModalPremium
        dealId={task.deal_id || null}
        open={showDealModal}
        onOpenChange={setShowDealModal}
      />

      {/* Modal de detalhes da tarefa pai */}
      <TaskDetailsModal
        taskId={task.parent_task_id || null}
        open={showParentTaskModal}
        onOpenChange={setShowParentTaskModal}
      />

      {/* Modal de alteração de responsável */}
      {onUpdateTask && (
        <Dialog open={showAssigneeModal} onOpenChange={setShowAssigneeModal}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCircle className="h-5 w-5 text-primary" />
                Alterar Responsável
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-1.5">
              <Label className="text-sm font-medium">Selecionar responsável</Label>
              <SystemUserSelector
                value={newAssigneeId}
                onSelect={(userId) => setNewAssigneeId(userId)}
                placeholder="Selecionar responsável..."
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssigneeModal(false)} disabled={isSavingAssignee}>
                Cancelar
              </Button>
              <Button
                disabled={isSavingAssignee}
                onClick={async () => {
                  setIsSavingAssignee(true);
                  try {
                    const params: Record<string, any> = {};
                    if (newAssigneeId && newAssigneeId !== (task.assigned_to || null)) {
                      params.p_assigned_to = newAssigneeId;
                    } else if (!newAssigneeId && task.assigned_to) {
                      params.p_clear_assigned_to = true;
                    }
                    if (Object.keys(params).length > 0) {
                      await onUpdateTask(params);
                    }
                    setShowAssigneeModal(false);
                  } finally {
                    setIsSavingAssignee(false);
                  }
                }}
              >
                {isSavingAssignee && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
